import React, { useEffect, useState } from "react";
import { getWithTokenAPI, deleteWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  TableContainer,
  IconButton,
  Divider,
  Pagination,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";


const Cart = () => {
  const [cartData, setCartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [cartsPerPage] = useState(10);

  // Colors
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const darkGreen = "#b02a37";
  const secondaryTextColor = "#6c757d";

  const navigate = useNavigate();

  // Fetch cart items
  const fetchCartItems = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, "/cart/find-all");

      if (res.statusCode === 200) {
        setCartData(res.data.Cart || []);
      } else {
        toast.error(res.message || "Failed to load cart items.");
        setCartData([]);
      }
    } catch (error) {
      console.error("Fetch cart error:", error);
      toast.error("Failed to load cart due to a network error.");
      setCartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastCart = currentPage * cartsPerPage;
  const indexOfFirstCart = indexOfLastCart - cartsPerPage;
  const currentCarts = cartData.slice(indexOfFirstCart, indexOfLastCart);
  const totalPages = Math.ceil(cartData.length / cartsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSellerClick = (sellerId) => {
    if (sellerId) {
      navigate(`/backoffice/seller-details/${sellerId}`);
    }
  };

  // View cart details
  const handleViewCart = (cart) => {
    setSelectedCart(cart);
    setOpenDialog(true);
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchCartItems();
  }, []);

  return (
    <Box
      sx={{
        p: 3,
        width: "100%",
      }}
    >
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <ShoppingCartIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Cart
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            View cart items
          </Typography>
        </Box>
      </Box>

      {/* Main Content - Replaced with the new table design */}
      <div className="table-container border rounded">
        <div className="table-responsive">
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress style={{ color: primaryGreen }} />
              <Typography sx={{ ml: 2, color: primaryGreen }}>Loading cart items...</Typography>
            </Box>
          ) : cartData.length > 0 ? (
            <table className="table m-0 resource-head">
              {/* Removed text-left here as we will apply to th/td directly for consistency */}
              <thead
                className="sticky-header fs-6"
                style={{ backgroundColor: lightGreen }}
              >
                <tr>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    S.No
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    User
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Seller Name
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Items
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Subtotal(Ksh)
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Tax(Ksh)
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Total Price(Ksh)
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Status
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Date
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentCarts.map((cart, index) => (
                  <tr key={cart._id} style={{ verticalAlign: 'middle' }}>
                    <td style={{ textAlign: "left" }}>
                      {indexOfFirstCart + index + 1}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: primaryGreen,
                            width: 32,
                            height: 32,
                            mr: 1,
                            fontSize: 14,
                          }}
                        >
                          {cart.user_id?.name?.charAt(0) || "U"}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            onClick={() =>
                              navigate(
                                `/backoffice/reviewuser/${cart.user_id?._id}`
                              )
                            }
                            style={{
                              fontWeight: "bold",
                              cursor: "pointer",
                              color: primaryGreen,
                              textDecoration: "none",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.textDecoration = "underline")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.textDecoration = "none")
                            }
                          >
                            {cart.user_id?.name || "N/A"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {cart.user_id?.email || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </td>
                    <td style={{ textAlign: "left" }}>
                      {cart.cart &&
                        cart.cart.length > 0 &&
                        cart.cart[0].product_id?.seller_id?.name ? (
                        <span
                          onClick={() =>
                            handleSellerClick(
                              cart.cart[0].product_id?.seller_id?._id
                            )
                          }
                          style={{
                            cursor: "pointer",
                            color: primaryGreen,
                            textDecoration: "none",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.textDecoration = "underline")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.textDecoration = "none")
                          }
                          title={`View details for ${cart.cart[0].product_id?.seller_id?.name}`}
                        >
                          {cart.cart[0].product_id?.seller_id?.name}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      {cart.cart?.length || 0} item(s)
                    </td>

                    <td style={{ textAlign: "left" }}>
                      {cart.total_Price?.toFixed(2) || "0.00"}
                    </td>

                    <td style={{ textAlign: "left" }}>
                      {cart.tax?.toFixed(2) || "0.00"}
                    </td>

                    <td style={{ textAlign: "left" }}>
                      {((Number(cart.total_Price) || 0) + (Number(cart.tax) || 0)).toFixed(2)}
                    </td>

                    <td style={{ textAlign: "left" }}>
                      <Chip
                        label={cart.status || "Pending"}
                        size="small"
                        sx={{
                          backgroundColor:
                            cart.status === "Completed"
                              ? lightGreen
                              : "#fff3e0",
                          color:
                            cart.status === "Completed" ? darkGreen : "#e65100",
                        }}
                      />
                    </td>
                    <td style={{ textAlign: "left" }}>
                      {new Date(cart.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      <IconButton
                        onClick={() => handleViewCart(cart)}
                        sx={{ color: primaryGreen }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                textAlign: "center",
              }}
            >
              <ShoppingCartIcon
                sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="h6" sx={{ mb: 1, color: "text.disabled" }}>
                No Cart items found
              </Typography>
            </Box>
          )}
        </div>
      </div>

      {/* Pagination */}
      {cartData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            mt: 3,
            pb: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="green"
            sx={{
              "& .MuiPaginationItem-root": {
                color: primaryGreen,
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: primaryGreen,
                color: "white",
              },
            }}
          />
        </Box>
      )}

      {/* Cart Detail Dialog (remains Material-UI) */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        sx={{
          "& .MuiDialog-paper": {
            m: 0,  // Remove margin
            width: "100%",  // Full width
            maxWidth: "100%",  // Override maxWidth
          }
        }}
        fullWidth
        fullScreen

      // maxWidth="md"
      // fullWidth
      >
        {selectedCart && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: primaryGreen,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
              >
                <PersonIcon sx={{ mr: 1 }} />  Cart Details
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => setOpenDialog(false)}
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}
                  sx={{
                    width: '20%'
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${lightGreen}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: darkGreen,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <PersonIcon sx={{ mr: 1 }} />
                      User Information
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        src={selectedCart.user_id?.profile_image}
                        sx={{ width: 60, height: 60, mr: 2 }}
                      >
                        {selectedCart.user_id?.name?.charAt(0) || "U"}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {selectedCart.user_id?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          ID: {selectedCart.user_id?._id || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ pl: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <EmailIcon sx={{ color: darkGreen, mr: 1 }} />
                        <Typography variant="body2">
                          {selectedCart.user_id?.email || "N/A"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <PhoneIcon sx={{ color: darkGreen, mr: 1 }} />
                        <Typography variant="body2">
                          {selectedCart.user_id?.phone_number || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Order Summary */}
                <Grid item xs={12} md={6}
                  sx={{
                    width: '75%'
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${lightGreen}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: darkGreen,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ReceiptIcon sx={{ mr: 1 }} />
                      Order Summary
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <span>Order ID:</span>
                        <span style={{ fontWeight: "bold" }}>
                          {selectedCart._id}
                        </span>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <span>Status:</span>
                        <Chip
                          label={selectedCart.status || "Pending"}
                          size="small"
                          sx={{
                            backgroundColor:
                              selectedCart.status === "Completed"
                                ? lightGreen
                                : "#fff3e0",
                            color:
                              selectedCart.status === "Completed"
                                ? darkGreen
                                : "#e65100",
                          }}
                        />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <span>Created:</span>
                        <span>
                          {new Date(selectedCart.createdAt).toLocaleString()}
                        </span>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <span>Updated:</span>
                        <span>
                          {new Date(selectedCart.updatedAt).toLocaleString()}
                        </span>
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: lightGreen,
                        p: 2,
                        borderRadius: 1,
                        borderLeft: `3px solid ${primaryGreen}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <span>Subtotal(Ksh):</span>
                        <span>
                          
                          {(
                            selectedCart.total_Price
                          ).toFixed(2)}
                        </span>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <span>Tax(Ksh):</span>
                        <span>{selectedCart.tax?.toFixed(2) || "0.00"}</span>
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontWeight: "bold",
                        }}
                      >
                        <span>Total(Ksh):</span>
                        <span>
                          {((Number(selectedCart.total_Price) || 0) + (Number(selectedCart.tax) || 0)).toFixed(2)}
                        </span>
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Cart Items (remains Material-UI Table within the dialog) */}
                <Grid item xs={12}
                  sx={{
                    width: '100%'
                  }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: darkGreen,
                      display: "flex",
                      alignItems: "center",

                    }}
                  >
                    <ShoppingCartIcon sx={{ mr: 1 }} />
                    Cart Items ({selectedCart.cart?.length || 0})
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <table size="small" className="table">
                      <thead>
                        <tr sx={{ backgroundColor: lightGreen }}>
                          <th style={{ textAlign: "left" }}>Product</th>
                          <th style={{ textAlign: "left" }}>Seller</th>
                          <th style={{ textAlign: "left" }}>Price(Ksh)</th>
                          <th style={{ textAlign: "left" }}>Qty</th>
                          <th style={{ textAlign: "left" }}>Total(Ksh)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCart.cart?.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ textAlign: "left" }}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Avatar
                                  src={item.product_id?.product_image?.[0]}
                                  variant="rounded"
                                  sx={{ width: 40, height: 40, mr: 2 }}
                                >
                                  {item.product_id?.name?.charAt(0) || "P"}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    {item.product_id?.name || "N/A"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    ID: {item.product_id?._id || "N/A"}
                                  </Typography>
                                </Box>
                              </Box>
                            </td>
                            <td style={{ textAlign: "left" }}>
                              <Typography variant="body2">
                                {item.product_id?.seller_id?.name || "N/A"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {item.product_id?.seller_id?.email || "N/A"}
                              </Typography>
                            </td>
                            <td style={{ textAlign: "left" }}>
                              <Typography variant="body2">
                                {item.discount_price?.toFixed(2) || "0.00"}
                              </Typography>
                              {item.product_id?.original_price && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textDecoration: "line-through",
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  {item.product_id.original_price.toFixed(2)}
                                </Typography>
                              )}
                            </td>
                            <td style={{ textAlign: "left" }}>
                              {item.quantity || 1}
                            </td>
                            <td style={{ textAlign: "left" }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                
                                {(item.discount_price * item.quantity)?.toFixed(
                                  2
                                ) || "0.00"}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableContainer>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenDialog(false)}
                variant="outlined"
                sx={{ color: darkGreen, borderColor: darkGreen }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog (remains Material-UI) */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
      >
        <DialogTitle sx={{ color: "#d32f2f" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this cart? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDelete(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cart;